<template>
  <div class="magic-book-stage">
    <!-- ===== 星野背景 ===== -->
    <div class="sky">
      <span v-for="s in stars" :key="s.id" class="star" :class="{ big: s.big }" :style="s.style"></span>
      <span v-for="m in meteors" :key="'m' + m.id" class="meteor" :style="m.style"></span>
    </div>
    <div class="nebula"></div>
    <div class="floor-glow"></div>

    <!-- ===== 顶部栏 ===== -->
    <div class="hud">
      <div class="hud-left">
        <svg class="hud-star" viewBox="0 0 24 24" aria-hidden="true">
          <path d="M12 2l2.4 7.2L22 12l-7.6 2.8L12 22l-2.4-7.2L2 12l7.6-2.8z" fill="currentColor" />
        </svg>
        <span class="hud-title">少女魔法书 · 星尘手账</span>
      </div>
      <div class="hud-right">
        <button v-if="bookState === 'reading' && spreadHasKey" class="hud-btn" @click="activateBlossom">
          <svg class="hud-ico" viewBox="0 0 24 24" aria-hidden="true">
            <path d="M12 2l2.4 7.2L22 12l-7.6 2.8L12 22l-2.4-7.2L2 12l7.6-2.8z" fill="currentColor" />
          </svg>
          立绘
        </button>
        <button class="hud-btn" @click="toggleSound">
          <svg v-if="soundOn" class="hud-ico" viewBox="0 0 24 24" aria-hidden="true">
            <path d="M4 9v6h4l5 4V5L8 9H4z" fill="currentColor" />
            <path d="M16.5 8.5a5 5 0 010 7M19 6a8 8 0 010 12" stroke="currentColor" stroke-width="1.6" fill="none" stroke-linecap="round" />
          </svg>
          <svg v-else class="hud-ico" viewBox="0 0 24 24" aria-hidden="true">
            <path d="M4 9v6h4l5 4V5L8 9H4z" fill="currentColor" />
            <path d="M16 9l6 6M22 9l-6 6" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" />
          </svg>
          {{ soundOn ? '音效' : '静音' }}
        </button>
        <button v-if="bookState !== 'cover'" class="hud-btn" @click="bookState === 'blossom' ? closeBlossom() : closeBook()">
          <svg class="hud-ico" viewBox="0 0 24 24" aria-hidden="true">
            <path d="M5 5h14M7 5v14a2 2 0 002 2h6a2 2 0 002-2V5M9 5V3h6v2" stroke="currentColor" stroke-width="1.6" fill="none" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
          合上
        </button>
      </div>
    </div>

    <!-- ===== 底部提示 ===== -->
    <div class="footer-hint">
      <template v-if="bookState === 'reading'">
        <span class="fh-dot"></span> 第 {{ leftIndex + 1 }} 页 · 共 {{ pages.length }} 页
        <span class="fh-star">✦</span> 点击左页向前 · 点击右页向后
      </template>
      <template v-else-if="bookState === 'blossom'">轻触底座 · 合上书页，魔法便会睡去</template>
      <template v-else>轻触封面 · 开启魔法</template>
    </div>

    <!-- ===== 书 ===== -->
    <div class="book-shell" :class="{ 'is-open': bookState !== 'cover', 'key-glow': keyGlow && bookState === 'reading', toppling: toppling }">
      <!-- 书脊 -->
      <div class="spine" :class="{ 'spine-open': bookState !== 'cover' }">
        <span class="spine-line"></span>
      </div>

      <!-- 合上的封面 -->
      <div class="book-closed" :class="{ opened: bookState !== 'cover', 'cover-hidden': coverGone }" @click="openBook">
        <div class="cover">
          <div class="cover-face front">
            <div class="cf-border"></div>
            <svg class="cf-corner tl" viewBox="0 0 64 64" aria-hidden="true">
              <path d="M4 44 Q4 16 46 6 Q20 14 14 34 Q10 44 6 50 Q3 52 4 44 Z" fill="none" stroke="url(#goldg)" stroke-width="2.6" />
              <path d="M4 46 Q6 30 30 10" fill="none" stroke="url(#goldg)" stroke-width="1.2" opacity=".7" />
            </svg>
            <svg class="cf-corner tr" viewBox="0 0 64 64" aria-hidden="true">
              <path d="M60 44 Q60 16 18 6 Q44 14 50 34 Q54 44 58 50 Q61 52 60 44 Z" fill="none" stroke="url(#goldg)" stroke-width="2.6" />
              <path d="M60 46 Q58 30 34 10" fill="none" stroke="url(#goldg)" stroke-width="1.2" opacity=".7" />
            </svg>
            <svg class="cf-corner bl" viewBox="0 0 64 64" aria-hidden="true">
              <path d="M4 20 Q4 48 46 58 Q20 50 14 30 Q10 20 6 14 Q3 12 4 20 Z" fill="none" stroke="url(#goldg)" stroke-width="2.6" />
              <path d="M4 18 Q6 34 30 54" fill="none" stroke="url(#goldg)" stroke-width="1.2" opacity=".7" />
            </svg>
            <svg class="cf-corner br" viewBox="0 0 64 64" aria-hidden="true">
              <path d="M60 20 Q60 48 18 58 Q44 50 50 30 Q54 20 58 14 Q61 12 60 20 Z" fill="none" stroke="url(#goldg)" stroke-width="2.6" />
              <path d="M60 18 Q58 34 34 54" fill="none" stroke="url(#goldg)" stroke-width="1.2" opacity=".7" />
            </svg>
            <svg width="0" height="0" aria-hidden="true">
              <defs>
                <linearGradient id="goldg" x1="0" y1="0" x2="1" y2="1">
                  <stop offset="0" stop-color="#f7e6a8" />
                  <stop offset=".5" stop-color="#d4af37" />
                  <stop offset="1" stop-color="#a37c24" />
                </linearGradient>
              </defs>
            </svg>

            <div class="cf-body">
              <div class="cf-eyebrow">A GIRL'S MAGIC JOURNAL</div>
              <h1 class="cf-title">星尘魔法书</h1>
              <div class="cf-titleline">
                <span class="line"></span>
                <svg class="flourish" viewBox="0 0 40 16" aria-hidden="true">
                  <path d="M20 8 C12 2 4 4 2 8 C4 12 12 14 20 8 Z" fill="none" stroke="url(#goldg)" stroke-width="1" />
                  <path d="M20 8 C28 2 36 4 38 8 C36 12 28 14 20 8 Z" fill="none" stroke="url(#goldg)" stroke-width="1" />
                  <path d="M20 4v8M17 6l3 2 3-2" stroke="url(#goldg)" stroke-width="1" fill="none" />
                </svg>
                <span class="line"></span>
              </div>

              <div class="cf-art">
                <div class="cf-art-inner">
                  <img :src="coverGirl" alt="星空中的魔法少女" class="cf-art-img" />
                  <span class="cf-art-shine"></span>
                </div>
                <span class="cf-art-stud tl"></span>
                <span class="cf-art-stud tr"></span>
                <span class="cf-art-stud bl"></span>
                <span class="cf-art-stud br"></span>
              </div>

              <div class="cf-window">
                <div class="cf-window-hole">
                  <div class="cf-window-page"></div>
                  <svg class="cf-circle ring-outer" viewBox="0 0 200 200" aria-hidden="true">
                    <circle cx="100" cy="100" r="88" fill="none" stroke="#d9b451" stroke-width="2" stroke-dasharray="7 12" />
                    <circle cx="100" cy="100" r="80" fill="none" stroke="#c9a23c" stroke-width="1" stroke-dasharray="2 6" opacity=".8" />
                  </svg>
                  <svg class="cf-circle ring-inner" viewBox="0 0 200 200" aria-hidden="true">
                    <circle cx="100" cy="100" r="58" fill="none" stroke="#e8cd7d" stroke-width="1.4" stroke-dasharray="26 10" />
                    <circle cx="100" cy="100" r="46" fill="none" stroke="#d9b451" stroke-width="1" opacity=".75" />
                  </svg>
                  <svg class="cf-circle pentagram" viewBox="0 0 200 200" aria-hidden="true">
                    <polygon points="100,36 137.6,151.8 39.1,80.2 160.9,80.2 62.4,151.8" fill="none" stroke="#f3d98c" stroke-width="1.6" stroke-linejoin="round" />
                  </svg>
                  <span class="cf-window-glow"></span>
                  <span class="cf-window-core"></span>
                </div>
              </div>

              <div class="cf-foot">
                <span class="cf-foot-text">少女魔法手账 · 星尘纪年</span>
                <span class="cf-seal">星</span>
              </div>
            </div>
          </div>

          <div class="cover-face back">
            <div class="cb-pattern"></div>
            <svg class="cb-circle" viewBox="0 0 200 200" aria-hidden="true">
              <circle cx="100" cy="100" r="86" fill="none" stroke="rgba(212,175,55,.35)" stroke-width="1.6" stroke-dasharray="5 9" />
              <circle cx="100" cy="100" r="64" fill="none" stroke="rgba(212,175,55,.25)" stroke-width="1" />
              <polygon points="100,42 133.8,150.4 44.3,84.5 155.7,84.5 66.2,150.4" fill="none" stroke="rgba(212,175,55,.28)" stroke-width="1" />
            </svg>
            <div class="cb-title">星尘魔法书</div>
            <div class="cb-sub">STARDUST GRIMOIRE</div>
          </div>
        </div>
      </div>

      <!-- 展开的书页 -->
      <div class="book-open-view" v-show="bookState !== 'cover'">
        <div class="spread">
          <div class="page page-left" @click="prevPage">
            <div class="page-inner">
              <PageContent :page="pages[leftIndex]" @jump="jumpTo" @awaken="activateBlossom" @preview="previewUrl = $event" />
            </div>
          </div>
          <div class="page page-right" @click="nextPage">
            <div class="page-inner">
              <PageContent :page="pages[leftIndex + 1]" @jump="jumpTo" @awaken="activateBlossom" @preview="previewUrl = $event" />
            </div>
          </div>

          <div class="flip-anim" :class="{ fwd: flipDir === 1, bwd: flipDir === -1, flipping: flipping }" v-show="flipping" @animationend="onFlipEnd">
            <div class="flip-face front">
              <div class="page-inner" v-if="flipPage">
                <PageContent :page="flipPage" @jump="jumpTo" @awaken="activateBlossom" @preview="previewUrl = $event" />
              </div>
            </div>
            <div class="flip-face back">
              <div class="flip-back-paper">
                <svg class="flip-back-circle" viewBox="0 0 200 200" aria-hidden="true">
                  <circle cx="100" cy="100" r="80" fill="none" stroke="rgba(180,140,70,.28)" stroke-width="1.4" stroke-dasharray="4 8" />
                  <polygon points="100,48 129.8,146.7 50.9,87.4 149.1,87.4 70.2,146.7" fill="none" stroke="rgba(180,140,70,.22)" stroke-width="1" />
                </svg>
              </div>
            </div>
          </div>

          <div class="dust-layer" ref="dustLayer"></div>
        </div>
      </div>

    </div>

    <!-- ===== 立绘空间（书倒下后的底座舞台） ===== -->
    <div class="blossom-space" v-show="bookState === 'blossom'" @click="closeBlossom">
      <span class="blossom-base" :class="{ 'blossom-fade': blossomClosing }"></span>
      <canvas ref="treeCanvas" class="tree-canvas" :class="{ 'tree-fade': blossomClosing }"></canvas>
      <div class="girls-wrap" :class="{ 'girls-sinking': blossomClosing }">
        <div class="girl-shadow"></div>
        <img :src="girlsTwo" alt="樱花树下的两位魔法少女" class="girls-img" />
      </div>
      <div class="front-petals" :class="{ 'petals-fade': blossomClosing }">
        <span v-for="p in frontPetals" :key="p.id" class="front-petal" :style="p.style"></span>
      </div>
      <div class="blossom-hint" :class="{ 'hint-fade': blossomClosing }">✦ 轻触底座 · 合上书页 ✦</div>
    </div>

    <!-- ===== 图片大图预览 ===== -->
    <transition name="pv">
      <div v-if="previewUrl" class="preview-mask" @click="previewUrl = ''">
        <img :src="previewUrl" class="preview-img" alt="大图预览" />
        <span class="preview-tip">✦ 轻触任意处 · 关闭预览 ✦</span>
      </div>
    </transition>
  </div>
</template>

<script>
import PageContent from './PageContent.vue'
import coverGirl from '@/assets/girlmagic/cover-girl.png'
import silhouetteStar from '@/assets/girlmagic/silhouette-star.png'
import silhouetteMoon from '@/assets/girlmagic/silhouette-moon.png'
import silhouetteFlower from '@/assets/girlmagic/silhouette-flower.png'
import silhouetteSnow from '@/assets/girlmagic/silhouette-snow.png'
import sceneSnow from '@/assets/girlmagic/scene-snow.png'
import sceneClock from '@/assets/girlmagic/scene-clock.png'
import sceneTea from '@/assets/girlmagic/scene-tea.png'
import sceneGlobe from '@/assets/girlmagic/scene-globe.png'
import girlsTwo from '@/assets/girlmagic/girls-two.png'

export default {
  name: 'GirlMagicBook',
  components: { PageContent },
  data () {
    return {
      bookState: 'cover', // cover | reading | blossom
      leftIndex: 0,
      flipping: false,
      flipDir: 1,
      flipPage: null,
      toppling: false,
      blossomClosing: false,
      previewUrl: '',
      coverGone: false,
      keyGlow: false,
      soundOn: true,
      coverLock: false,
      audioCtx: null,
      stars: [],
      meteors: [],
      frontPetals: [],
      treeCtx: null,
      treeW: 0,
      treeH: 0,
      bloomStart: 0,
      petals: [],
      treeAnim: null,
      coverGirl,
      girlsTwo,
      pages: [
        { id: 0, type: 'toc', num: 1 },
        { id: 1, type: 'preface', num: 2, key: true },
        {
          id: 2, type: 'chapter', num: 3, no: '壹', title: '星之章', sub: '夜空的纺织者',
          attr: '星辉 · 祈愿与导航',
          poem: ['她把流星织进裙摆的银线里，', '迷路的旅人只要抬头，', '就能看见回家的路。'],
          silhouette: silhouetteStar, cls: 'ch-star'
        },
        {
          id: 3, type: 'journal', num: 4, kicker: '星之章 · 手账', title: '细雪森林的来信',
          scene: sceneSnow, sceneName: 'snow', decor: 'gear', cls: 'j-star',
          paras: [
            '传说午夜时分，雪林深处会亮起一颗不肯熄灭的星。',
            '那是很久以前，一位迷路的少女把愿望挂在路灯上，忘了取走。',
            '如果你路过那串小小的脚印——请替她许一个愿吧。',
            '星光会替你保密，就像这片森林，替所有人保管着童年的雪。'
          ],
          stamp: '星 · 壹', stampSub: '魔法邮局'
        },
        {
          id: 4, type: 'chapter', num: 5, no: '贰', title: '月之章', sub: '时间的守钟人',
          attr: '月光 · 时间与回溯',
          poem: ['钟楼顶端的月亮，是她停摆又倒转的心跳。', '指针每倒转一圈，', '就有一个“如果”，重新发芽。'],
          silhouette: silhouetteMoon, cls: 'ch-moon'
        },
        {
          id: 5, type: 'journal', num: 6, kicker: '月之章 · 手账', title: '云顶钟楼手记',
          scene: sceneClock, sceneName: 'clock', decor: 'rose', cls: 'j-moon',
          paras: [
            '云朵把钟楼托得很高很高，高到连时间也会迷路。',
            '琥珀色的钟摆下，少年与少女的约定被轻轻缝进云里。',
            '月之章的魔法少女说：重要的时刻不会真的消失，它们只是藏进了倒转的指针里，',
            '等某一天，你恰好抬起头来，就能重新遇见。'
          ],
          stamp: '月 · 贰', stampSub: '时间当铺'
        },
        {
          id: 6, type: 'chapter', num: 7, no: '叁', title: '花之章', sub: '茶会与铃兰',
          attr: '花语 · 治愈与盛放',
          poem: ['她邀请每一位疲惫的旅人坐下，', '喝一杯不会凉的红茶。', '花瓣落下的时候，', '烦恼便悄悄开成了花。'],
          silhouette: silhouetteFlower, cls: 'ch-flower'
        },
        {
          id: 7, type: 'journal', num: 8, kicker: '花之章 · 手账', title: '不会凉的红茶',
          scene: sceneTea, sceneName: 'tea', decor: 'familiar', cls: 'j-flower',
          paras: [
            '午后的茶会，是没有结束的时候的。',
            '使魔先生捧着刚出炉的小蛋糕，在蕾丝桌布上留下一串奶油脚印。',
            '红茶冒着永远的热气，杯底沉着细碎的星光——',
            '据说，喝下这杯茶的人会被花之章记住很久很久，久到下一次烦恼来临前，嘴角都是甜的。'
          ],
          stamp: '花 · 叁', stampSub: '铃兰茶室'
        },
        {
          id: 8, type: 'chapter', num: 9, no: '肆', title: '雪之章', sub: '永不融化的冬天',
          attr: '霜雪 · 冻结与守护',
          poem: ['她把冬天做成透明的标本，', '让那些想被记住的瞬间，', '永远停在最美的那一刻。'],
          silhouette: silhouetteSnow, cls: 'ch-snow'
        },
        {
          id: 9, type: 'journal', num: 10, kicker: '雪之章 · 手账', title: '雪晶球里的城堡',
          scene: sceneGlobe, sceneName: 'globe', decor: 'snowstar', cls: 'j-snow',
          paras: [
            '这座城堡已经下了三百年的雪，却没有一片雪花感到孤单。',
            '雪之章的魔法少女把它装进水晶球里，这样，路过的人伸出手，就能捧住一整座不会融化的故乡。',
            '窗口的灯还亮着——那是在等你，',
            '在风雪里想起某个人时，轻轻摇一摇。'
          ],
          stamp: '雪 · 肆', stampSub: '永冬邮局'
        },
        {
          id: 10, type: 'ending', num: 11, key: true,
          lines: [
            '合上这本书之前，请再记得三件事：',
            '星屑会记得你的名字；',
            '时钟会为你倒转一次；',
            '而樱花，会替我们说再见，也说你好。'
          ]
        },
        {
          id: 11, type: 'colophon', num: 12,
          title: '星尘魔法书',
          sub: '第一卷 · 完',
          lines: [
            '风会记得每一页翻过的声音，',
            '星屑会记得每一盏点亮的灯。',
            '而你翻开这本书的这一刻，',
            '已经成为了故事的一部分。'
          ],
          stamp: '星 · 终'
        }
      ]
    }
  },
  computed: {
    spreadHasKey () {
      const p = this.pages[this.leftIndex]
      const q = this.pages[this.leftIndex + 1]
      return !!(p && p.key) || !!(q && q.key)
    }
  },
  created () {
    this.buildStars()
    this.buildFrontPetals()
    this.preloadImages()
  },
  mounted () {
    window.addEventListener('keydown', this.onKeydown)
  },
  beforeDestroy () {
    window.removeEventListener('keydown', this.onKeydown)
    clearTimeout(this._coverTimer)
    clearTimeout(this._blossomTimer)
    clearTimeout(this._closeTimer)
    this.stopTree()
  },
  methods: {
    /* ---------------- 图片预加载 ---------------- */
    preloadImages () {
      const srcs = [
        this.coverGirl, this.girlsTwo,
        silhouetteStar, silhouetteMoon, silhouetteFlower, silhouetteSnow,
        sceneSnow, sceneClock, sceneTea, sceneGlobe
      ]
      for (let i = 0; i < srcs.length; i++) {
        const img = new Image()
        img.src = srcs[i]
      }
    },

    /* ---------------- 星空背景 ---------------- */
    buildStars () {
      const stars = []
      for (let i = 0; i < 90; i++) {
        const big = Math.random() < 0.06
        const size = big ? 2.4 + Math.random() * 1.4 : 1 + Math.random() * 1.3
        stars.push({
          id: i,
          big,
          style: {
            left: (Math.random() * 100).toFixed(2) + '%',
            top: (Math.random() * 100).toFixed(2) + '%',
            width: size.toFixed(2) + 'px',
            height: size.toFixed(2) + 'px',
            opacity: (0.35 + Math.random() * 0.65).toFixed(2),
            animationDelay: (Math.random() * 7).toFixed(2) + 's',
            animationDuration: (3 + Math.random() * 5).toFixed(2) + 's'
          }
        })
      }
      const meteors = []
      for (let i = 0; i < 2; i++) {
        meteors.push({
          id: i,
          style: {
            left: (55 + Math.random() * 40).toFixed(1) + '%',
            top: (5 + Math.random() * 22).toFixed(1) + '%',
            animationDelay: (6 + i * 9 + Math.random() * 6).toFixed(1) + 's',
            animationDuration: (5 + Math.random() * 3).toFixed(1) + 's'
          }
        })
      }
      this.stars = stars
      this.meteors = meteors
    },
    buildFrontPetals () {
      const petals = []
      for (let i = 0; i < 10; i++) {
        const size = 4 + Math.random() * 6
        petals.push({
          id: i,
          style: {
            left: (Math.random() * 100).toFixed(1) + '%',
            top: (-10 - Math.random() * 30).toFixed(1) + 'px',
            width: size.toFixed(1) + 'px',
            height: (size * 0.72).toFixed(1) + 'px',
            animationDelay: (Math.random() * 8).toFixed(2) + 's',
            animationDuration: (7 + Math.random() * 8).toFixed(2) + 's',
            opacity: (0.45 + Math.random() * 0.55).toFixed(2)
          }
        })
      }
      this.frontPetals = petals
    },

    /* ---------------- 书的状态 ---------------- */
    openBook () {
      if (this.bookState !== 'cover' || this.coverLock) return
      this.flipping = false
      this.flipPage = null
      this.bookState = 'reading'
      this.coverLock = true
      this.coverGone = false
      clearTimeout(this._coverTimer)
      this._coverTimer = setTimeout(() => {
        this.coverLock = false
        this.coverGone = true
      }, 1250)
      this.playFlip()
      this.playChime(2, 0.04)
      this.checkKeyGlow()
    },
    closeBook () {
      if (this.bookState === 'blossom') { this.closeBlossom(); return }
      if (this.bookState !== 'reading') return
      // 先让封面可见（停在翻开角度），再转回合上，避免左侧空白
      this.coverGone = false
      this.bookState = 'cover'
      // 合上后再次翻开，回到目录页
      this.leftIndex = 0
      this.keyGlow = false
      this.playCloseChime()
    },

    /* ---------------- 翻页 ---------------- */
    nextPage () {
      if (this.flipping || this.coverLock || this.bookState !== 'reading') return
      if (this.leftIndex + 3 >= this.pages.length) { this.closeBook(); return }
      this.flipDir = 1
      this.flipPage = this.pages[this.leftIndex + 1]
      // 翻页一开始就切换到目标展开页：纸片翻起后，下方已是下一页内容
      this.leftIndex = Math.min(this.leftIndex + 2, this.pages.length - 2)
      this.flipping = true
      this.playFlip()
      this.playChime(2, 0.045)
      this.spawnStarDust()
    },
    prevPage () {
      if (this.flipping || this.coverLock || this.bookState !== 'reading') return
      if (this.leftIndex < 2) { this.closeBook(); return }
      this.flipDir = -1
      this.flipPage = this.pages[this.leftIndex]
      // 翻页一开始就切换到目标展开页：纸片翻起后，下方已是上一页内容
      this.leftIndex = Math.max(this.leftIndex - 2, 0)
      this.flipping = true
      this.playFlip()
      this.playChime(2, 0.045)
      this.spawnStarDust()
    },
    onFlipEnd (e) {
      // 只响应翻页元素自身的动画结束，过滤子页面元素的动画冒泡
      if (!this.flipping) return
      if (e && e.target !== e.currentTarget) return
      this.flipping = false
      this.flipPage = null
      this.checkKeyGlow()
    },
    jumpTo (pageId) {
      if (this.flipping || this.coverLock || this.bookState !== 'reading') return
      let li = pageId % 2 === 0 ? pageId : pageId - 1
      li = Math.max(0, Math.min(li, this.pages.length - 2))
      if (li === this.leftIndex) { this.playChime(2, 0.04); return }
      this.leftIndex = li
      this.playChime(3, 0.05)
      this.spawnStarDust()
      this.checkKeyGlow()
    },
    checkKeyGlow () {
      const p = this.pages[this.leftIndex]
      const q = this.pages[this.leftIndex + 1]
      const hot = !!(p && (p.type === 'chapter' || p.type === 'ending')) ||
        !!(q && (q.type === 'chapter' || q.type === 'ending'))
      if (hot && !this.keyGlow) this.playResonance()
      this.keyGlow = !!hot
    },

    /* ---------------- 星光特效 ---------------- */
    spawnStarDust () {
      const layer = this.$refs.dustLayer
      if (!layer) return
      const count = 8
      for (let i = 0; i < count; i++) {
        const s = document.createElement('span')
        s.className = 'dust-star'
        const size = 2.5 + Math.random() * 5
        const dx = (Math.random() - 0.5) * 260
        const dy = (Math.random() - 0.5) * 210
        const gold = Math.random() < 0.5
        s.style.cssText = 'left:50%;top:50%;width:' + size + 'px;height:' + size + 'px;--dx:' + dx + 'px;--dy:' + dy + 'px;--rot:' + Math.floor(Math.random() * 360) + 'deg;animation-delay:' + (Math.random() * 0.12).toFixed(2) + 's;' + (gold ? 'background:radial-gradient(circle,#fff 0%,#ffe9b0 45%,rgba(255,233,176,0) 72%);box-shadow:0 0 8px 2px rgba(255,240,190,.55);' : 'background:radial-gradient(circle,#fff 0%,#cfd9ff 45%,rgba(207,217,255,0) 72%);box-shadow:0 0 8px 2px rgba(207,217,255,.5);')
        layer.appendChild(s)
        setTimeout(() => { if (s.parentNode) s.parentNode.removeChild(s) }, 1500)
      }
    },

    /* ---------------- 立绘空间 ---------------- */
    activateBlossom () {
      // 仅在立绘页触发
      if (this.bookState !== 'reading' || !this.spreadHasKey) return
      this.playBlossom()
      // 书本向后倒下，化作底座舞台
      this.toppling = true
      clearTimeout(this._blossomTimer)
      this._blossomTimer = setTimeout(() => {
        this.bookState = 'blossom'
        this.$nextTick(() => {
          this.initTree()
        })
      }, 720)
    },
    closeBlossom () {
      if (this.bookState !== 'blossom' || this.blossomClosing) return
      this.playCloseChime()
      this.blossomClosing = true
      // 书本绕底部直立还原（移除倒下姿态，沿底部轴过渡回正立）
      this.toppling = false
      clearTimeout(this._closeTimer)
      this._closeTimer = setTimeout(() => {
        this.blossomClosing = false
        this.bookState = 'reading'
        this.stopTree()
      }, 950)
    },

    /* ---------------- 水晶樱花树 Canvas ---------------- */
    initTree () {
      const canvas = this.$refs.treeCanvas
      if (!canvas) return
      const dpr = Math.min(window.devicePixelRatio || 1, 2)
      const rect = canvas.parentNode.getBoundingClientRect()
      canvas.width = Math.round(rect.width * dpr)
      canvas.height = Math.round(rect.height * dpr)
      canvas.style.width = rect.width + 'px'
      canvas.style.height = rect.height + 'px'
      const ctx = canvas.getContext('2d')
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
      this.treeCtx = ctx
      this.treeW = rect.width
      this.treeH = rect.height
      // 底座出现后再开始生长
      this.bloomStart = performance.now() + 500
      this.petals = []
      for (let i = 0; i < 64; i++) this.petals.push(this.makePetal(true))
      this.startTreeAnim()
    },
    makePetal (anywhere) {
      return {
        x: Math.random() * this.treeW,
        y: anywhere ? Math.random() * this.treeH : -20,
        size: 3.5 + Math.random() * 6,
        vy: 0.55 + Math.random() * 1.15,
        vx: (Math.random() - 0.5) * 0.5,
        sway: Math.random() * Math.PI * 2,
        swaySpeed: 0.02 + Math.random() * 0.04,
        rot: Math.random() * Math.PI * 2,
        rotSpeed: (Math.random() - 0.5) * 0.05,
        alpha: 0.45 + Math.random() * 0.5,
        pink: Math.random() > 0.3
      }
    },
    startTreeAnim () {
      this.stopTree()
      const loop = () => {
        this.drawTree()
        this.treeAnim = requestAnimationFrame(loop)
      }
      this.treeAnim = requestAnimationFrame(loop)
    },
    stopTree () {
      if (this.treeAnim) {
        cancelAnimationFrame(this.treeAnim)
        this.treeAnim = null
      }
      this.treeCtx = null
    },
    drawTree () {
      const ctx = this.treeCtx
      if (!ctx || !this.treeW) return
      const w = this.treeW
      const h = this.treeH
      ctx.clearRect(0, 0, w, h)
      const t = (performance.now() - this.bloomStart) / 1000
      const grow = Math.min(1, t / 2.4)
      const ease = 1 - Math.pow(1 - grow, 3)
      const bloomAlpha = Math.min(1, Math.max(0, (t - 1.1) / 1.3))

      // 底座微光
      const g0 = ctx.createRadialGradient(w / 2, h * 0.94, 10, w / 2, h * 0.94, w * 0.46)
      g0.addColorStop(0, 'rgba(214,182,255,' + (0.4 * ease).toFixed(3) + ')')
      g0.addColorStop(1, 'rgba(214,182,255,0)')
      ctx.fillStyle = g0
      ctx.fillRect(0, 0, w, h)

      // 树干与枝条（琥珀水晶质感）
      ctx.lineCap = 'round'
      this.branch(ctx, w / 2, h * 0.97, -Math.PI / 2, h * 0.34, 11, ease, bloomAlpha)

      // 花瓣
      for (let i = 0; i < this.petals.length; i++) {
        const p = this.petals[i]
        p.y += p.vy
        p.sway += p.swaySpeed
        p.x += p.vx + Math.sin(p.sway) * 0.45
        p.rot += p.rotSpeed
        if (p.y > h + 24) {
          this.petals[i] = this.makePetal(false)
          this.petals[i].x = Math.random() * w
          continue
        }
        this.drawPetal(ctx, p)
      }
    },
    branch (ctx, x, y, angle, len, width, ease, bloomAlpha) {
      if (len < 5 || width < 0.7) return
      const dl = len * ease
      if (dl < 1.5) return
      const ex = x + Math.cos(angle) * dl
      const ey = y + Math.sin(angle) * dl
      // 水晶琥珀渐变
      const grad = ctx.createLinearGradient(x, y, ex, ey)
      grad.addColorStop(0, 'rgba(120,66,26,' + (0.82 * ease).toFixed(3) + ')')
      grad.addColorStop(0.55, 'rgba(201,138,58,' + (0.85 * ease).toFixed(3) + ')')
      grad.addColorStop(1, 'rgba(255,206,120,' + (0.6 * ease).toFixed(3) + ')')
      ctx.strokeStyle = grad
      ctx.lineWidth = Math.max(0.6, width * ease)
      ctx.shadowColor = 'rgba(255,190,120,0.55)'
      ctx.shadowBlur = width > 3 ? 10 : 4
      ctx.beginPath()
      ctx.moveTo(x, y)
      ctx.lineTo(ex, ey)
      ctx.stroke()
      ctx.shadowBlur = 0

      // 水晶高光
      if (width > 4) {
        ctx.strokeStyle = 'rgba(255,236,190,0.5)'
        ctx.lineWidth = 1
        ctx.beginPath()
        ctx.moveTo(x + 1, y - 1)
        ctx.lineTo(ex + 1, ey - 1)
        ctx.stroke()
      }

      // 顶端花簇
      if (len < 14 && bloomAlpha > 0) {
        this.blossomCluster(ctx, ex, ey, width, bloomAlpha)
      }
      if (len < 7) return

      const childLen = len * (0.68 + Math.random() * 0.12)
      const spread = 0.42 + Math.random() * 0.22
      this.branch(ctx, ex, ey, angle - spread - Math.random() * 0.25, childLen, width * 0.62, ease, bloomAlpha)
      this.branch(ctx, ex, ey, angle + spread + Math.random() * 0.25, childLen, width * 0.62, ease, bloomAlpha)
      if (len > 24 && Math.random() < 0.55) {
        this.branch(ctx, ex, ey, angle + (Math.random() - 0.5) * 0.9, childLen * 0.78, width * 0.5, ease, bloomAlpha)
      }
    },
    blossomCluster (ctx, x, y, width, alpha) {
      const n = 3 + Math.floor(Math.random() * 4)
      for (let i = 0; i < n; i++) {
        const a = Math.random() * Math.PI * 2
        const r = 4 + Math.random() * 12
        const px = x + Math.cos(a) * r
        const py = y + Math.sin(a) * r * 0.7
        const s = 2 + Math.random() * 4
        const pink = Math.random() > 0.25
        ctx.fillStyle = pink
          ? 'rgba(255,183,205,' + (alpha * (0.5 + Math.random() * 0.4)).toFixed(3) + ')'
          : 'rgba(255,245,248,' + (alpha * (0.5 + Math.random() * 0.4)).toFixed(3) + ')'
        ctx.shadowColor = 'rgba(255,170,200,0.8)'
        ctx.shadowBlur = 7
        ctx.beginPath()
        ctx.arc(px, py, s, 0, Math.PI * 2)
        ctx.fill()
      }
      ctx.shadowBlur = 0
    },
    drawPetal (ctx, p) {
      const col = p.pink ? '255,176,202' : '255,240,244'
      ctx.save()
      ctx.translate(p.x, p.y)
      ctx.rotate(p.rot)
      ctx.fillStyle = 'rgba(' + col + ',' + p.alpha.toFixed(3) + ')'
      ctx.shadowColor = 'rgba(255,180,205,0.6)'
      ctx.shadowBlur = 6
      ctx.beginPath()
      ctx.ellipse(0, 0, p.size, p.size * 0.62, 0, 0, Math.PI * 2)
      ctx.fill()
      ctx.restore()
    },

    /* ---------------- Web Audio 音效 ---------------- */
    ensureCtx () {
      if (!this.audioCtx) {
        const AC = window.AudioContext || window.webkitAudioContext
        if (!AC) return null
        this.audioCtx = new AC()
      }
      if (this.audioCtx.state === 'suspended') this.audioCtx.resume()
      return this.audioCtx
    },
    tone (freq, t0, dur, type, gain) {
      const ctx = this.audioCtx
      if (!ctx) return
      const osc = ctx.createOscillator()
      const g = ctx.createGain()
      osc.type = type || 'sine'
      osc.frequency.value = freq
      g.gain.setValueAtTime(0.0001, t0)
      g.gain.exponentialRampToValueAtTime(Math.max(gain, 0.0002), t0 + 0.015)
      g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur)
      osc.connect(g)
      g.connect(ctx.destination)
      osc.start(t0)
      osc.stop(t0 + dur + 0.05)
    },
    playChime (count, vol) {
      if (!this.soundOn) return
      const ctx = this.ensureCtx()
      if (!ctx) return
      const t = ctx.currentTime
      const scale = [1046.5, 1174.66, 1318.51, 1567.98, 2093]
      const n = count || 3 + Math.floor(Math.random() * 3)
      const v = vol || 0.05
      for (let i = 0; i < n; i++) {
        const f = scale[Math.floor(Math.random() * scale.length)]
        this.tone(f, t + i * 0.055, 1.5, 'sine', v)
        this.tone(f * 2, t + i * 0.055, 0.9, 'triangle', v * 0.4)
      }
    },
    playFlip () {
      if (!this.soundOn) return
      const ctx = this.ensureCtx()
      if (!ctx) return
      const t = ctx.currentTime
      const len = Math.floor(ctx.sampleRate * 0.26)
      const buf = ctx.createBuffer(1, len, ctx.sampleRate)
      const data = buf.getChannelData(0)
      for (let i = 0; i < len; i++) data[i] = Math.random() * 2 - 1
      const src = ctx.createBufferSource()
      src.buffer = buf
      const bp = ctx.createBiquadFilter()
      bp.type = 'bandpass'
      bp.frequency.setValueAtTime(950, t)
      bp.frequency.exponentialRampToValueAtTime(380, t + 0.24)
      bp.Q.value = 0.9
      const g = ctx.createGain()
      g.gain.setValueAtTime(0.0001, t)
      g.gain.exponentialRampToValueAtTime(0.11, t + 0.02)
      g.gain.exponentialRampToValueAtTime(0.0001, t + 0.25)
      src.connect(bp)
      bp.connect(g)
      g.connect(ctx.destination)
      src.start(t)
    },
    playResonance () {
      if (!this.soundOn) return
      const ctx = this.ensureCtx()
      if (!ctx) return
      const t = ctx.currentTime
      const chord = [196, 246.94, 293.66, 392]
      for (let i = 0; i < chord.length; i++) {
        const osc = ctx.createOscillator()
        const g = ctx.createGain()
        osc.type = 'sine'
        osc.frequency.value = chord[i]
        g.gain.setValueAtTime(0.0001, t)
        g.gain.exponentialRampToValueAtTime(0.022, t + 0.7)
        g.gain.exponentialRampToValueAtTime(0.0001, t + 2.8)
        osc.connect(g)
        g.connect(ctx.destination)
        osc.start(t)
        osc.stop(t + 3)
      }
    },
    playBlossom () {
      if (!this.soundOn) return
      const ctx = this.ensureCtx()
      if (!ctx) return
      const t = ctx.currentTime
      const seq = [523.25, 659.25, 783.99, 1046.5, 1318.51, 1567.98, 2093]
      for (let i = 0; i < seq.length; i++) {
        this.tone(seq[i], t + i * 0.09, 1.3, 'sine', 0.045)
        this.tone(seq[i] * 2, t + i * 0.09, 0.85, 'triangle', 0.016)
      }
    },
    playCloseChime () {
      if (!this.soundOn) return
      const ctx = this.ensureCtx()
      if (!ctx) return
      const t = ctx.currentTime
      const seq = [1318.51, 1046.5, 783.99, 659.25]
      for (let i = 0; i < seq.length; i++) {
        this.tone(seq[i], t + i * 0.11, 1.1, 'sine', 0.04)
      }
    },
    toggleSound () {
      this.soundOn = !this.soundOn
      if (this.soundOn) this.playChime(2, 0.04)
    },

    /* ---------------- 键盘 ---------------- */
    onKeydown (e) {
      if (this.bookState !== 'reading') return
      if (e.key === 'ArrowRight') this.nextPage()
      if (e.key === 'ArrowLeft') this.prevPage()
    }
  }
}
</script>

<style>
/* ============ 字体（全局声明，供子组件共用） ============ */
@font-face {
  font-family: 'MoFa-ShuFa';
  src: url('~@/assets/fonts/书法毛笔.ttf') format('truetype');
  font-display: swap;
}
@font-face {
  font-family: 'MoFa-KangXi';
  src: url('~@/assets/fonts/康熙字典体.ttf') format('truetype');
  font-display: swap;
}
@font-face {
  font-family: 'MoFa-XiaoXing';
  src: url('~@/assets/fonts/天上一只小星星.ttf') format('truetype');
  font-display: swap;
}
:root {
  --font-brush: 'MoFa-ShuFa', 'Kaiti SC', 'STKaiti', cursive;
  --font-kx: 'MoFa-KangXi', 'Kaiti SC', 'STKaiti', serif;
  --font-star: 'MoFa-XiaoXing', 'Kaiti SC', cursive;
  --gold: #d4af37;
  --gold-light: #f3dc94;
  --gold-deep: #9a7420;
  --ink: #4a3723;
  --ink-soft: #6b543a;
}
</style>

<style scoped>
/* ================= 舞台与星野 ================= */
.magic-book-stage {
  position: fixed;
  inset: 0;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  background: radial-gradient(130% 95% at 50% 18%, #261b50 0%, #181040 42%, #0d0828 76%, #060413 100%);
  font-family: var(--font-kx);
  user-select: none;
  -webkit-user-select: none;
}
.sky {
  position: absolute;
  inset: 0;
  pointer-events: none;
}
.star {
  position: absolute;
  border-radius: 50%;
  background: #fff;
  box-shadow: 0 0 5px 1px rgba(210, 225, 255, 0.7);
  animation: twinkle ease-in-out infinite;
}
.star.big {
  box-shadow: 0 0 10px 3px rgba(255, 230, 180, 0.75);
}
@keyframes twinkle {
  0%, 100% { opacity: 0.25; transform: scale(0.82); }
  50% { opacity: 1; transform: scale(1.1); }
}
.meteor {
  position: absolute;
  width: 2px;
  height: 2px;
  border-radius: 50%;
  background: #fff;
  box-shadow: 0 0 6px 2px rgba(180, 220, 255, 0.55);
  animation: meteor-fly linear infinite;
  opacity: 0;
}
.meteor::after {
  content: '';
  position: absolute;
  width: 96px;
  height: 1px;
  right: 2px;
  top: 0;
  background: linear-gradient(90deg, rgba(255, 255, 255, 0), rgba(255, 255, 255, 0.6));
}
@keyframes meteor-fly {
  0% { transform: translate(0, 0); opacity: 0; }
  3% { opacity: 1; }
  13% { transform: translate(-52vw, 30vh); opacity: 0; }
  100% { transform: translate(-52vw, 30vh); opacity: 0; }
}
.nebula {
  position: absolute;
  inset: -20%;
  background:
    radial-gradient(38% 30% at 22% 24%, rgba(138, 92, 214, 0.22), transparent 70%),
    radial-gradient(42% 34% at 78% 30%, rgba(80, 120, 220, 0.16), transparent 70%),
    radial-gradient(46% 36% at 52% 86%, rgba(180, 90, 160, 0.14), transparent 70%);
  filter: blur(30px);
  animation: nebula-drift 70s ease-in-out infinite alternate;
  pointer-events: none;
}
@keyframes nebula-drift {
  from { transform: rotate(0deg) scale(1); }
  to { transform: rotate(9deg) scale(1.12); }
}
.floor-glow {
  position: absolute;
  left: 50%;
  bottom: -12%;
  width: 70vw;
  height: 34vh;
  transform: translateX(-50%);
  background: radial-gradient(ellipse at center, rgba(160, 120, 230, 0.28), transparent 68%);
  filter: blur(22px);
  pointer-events: none;
}

/* ================= 顶部栏 ================= */
.hud {
  position: absolute;
  top: 18px;
  left: 22px;
  right: 22px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  z-index: 60;
  pointer-events: none;
}
.hud-left {
  display: flex;
  align-items: center;
  gap: 9px;
  color: rgba(238, 226, 255, 0.9);
  text-shadow: 0 1px 6px rgba(0, 0, 0, 0.5);
}
.hud-star {
  width: 17px;
  height: 17px;
  color: var(--gold-light);
  filter: drop-shadow(0 0 6px rgba(243, 220, 148, 0.7));
}
.hud-title {
  font-family: var(--font-star);
  font-size: 15px;
  letter-spacing: 0.18em;
}
.hud-right {
  display: flex;
  gap: 10px;
  pointer-events: auto;
}
.hud-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 14px;
  border-radius: 999px;
  border: 1px solid rgba(230, 210, 255, 0.32);
  background: linear-gradient(180deg, rgba(64, 44, 108, 0.55), rgba(38, 24, 72, 0.62));
  color: rgba(240, 228, 255, 0.92);
  font-family: var(--font-star);
  font-size: 13px;
  letter-spacing: 0.08em;
  cursor: pointer;
  backdrop-filter: blur(8px);
  box-shadow: 0 4px 18px rgba(0, 0, 0, 0.35);
  transition: all 0.28s ease;
}
.hud-btn:hover {
  border-color: rgba(243, 220, 148, 0.65);
  color: #fff;
  box-shadow: 0 4px 24px rgba(212, 175, 55, 0.35);
  transform: translateY(-1px);
}
.hud-ico {
  width: 15px;
  height: 15px;
}

/* ================= 底部提示 ================= */
.footer-hint {
  position: absolute;
  bottom: 16px;
  left: 0;
  right: 0;
  text-align: center;
  font-family: var(--font-star);
  font-size: 13px;
  letter-spacing: 0.12em;
  color: rgba(226, 212, 248, 0.78);
  text-shadow: 0 1px 8px rgba(0, 0, 0, 0.6);
  z-index: 60;
  pointer-events: none;
}
.fh-dot {
  display: inline-block;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--gold-light);
  margin-right: 7px;
  box-shadow: 0 0 8px 2px rgba(243, 220, 148, 0.6);
  vertical-align: 1px;
}
.fh-star {
  margin: 0 9px;
  color: var(--gold-light);
}

/* ================= 书 ================= */
.book-shell {
  position: relative;
  width: min(1180px, 92vw, calc(80vh * 1.62));
  aspect-ratio: 16 / 10.4;
  perspective: 2600px;
  transform-style: preserve-3d;
  transform-origin: 50% 100%;
  transition: transform 0.9s cubic-bezier(0.5, 0.06, 0.2, 1), opacity 0.9s ease;
}
.book-shell.key-glow .spread::before {
  opacity: 1;
}
.book-shell.key-glow {
  animation: shell-breathe 3.6s ease-in-out infinite;
}
.book-shell.toppling {
  transform-origin: 50% 100%;
  transform: perspective(1500px) rotateX(84deg);
  opacity: 0.9;
}
@keyframes shell-breathe {
  0%, 100% { filter: drop-shadow(0 0 26px rgba(255, 190, 120, 0.16)); }
  50% { filter: drop-shadow(0 0 58px rgba(255, 180, 110, 0.34)); }
}

/* 书脊 */
.spine {
  position: absolute;
  left: calc(25% - 13px);
  top: 6px;
  bottom: 6px;
  width: 15px;
  border-radius: 6px 3px 3px 6px;
  background: linear-gradient(90deg, #191133, #2d1f4e 55%, #3a2a5e);
  box-shadow: inset -2px 0 4px rgba(0, 0, 0, 0.5), 2px 0 8px rgba(0, 0, 0, 0.45);
  z-index: 5;
}
.spine::before {
  content: '';
  position: absolute;
  left: 3px;
  right: 3px;
  top: 10px;
  bottom: 10px;
  border: 1px solid rgba(212, 175, 55, 0.35);
  border-radius: 3px;
}
.spine-line {
  position: absolute;
  top: 22%;
  bottom: 22%;
  left: 50%;
  width: 1px;
  background: linear-gradient(180deg, transparent, rgba(212, 175, 55, 0.5), transparent);
}
.spine-open {
  opacity: 0.55;
}

/* ------- 封面（合上） ------- */
.book-closed {
  position: absolute;
  left: 25%;
  top: 0;
  bottom: 0;
  width: 50%;
  transform-style: preserve-3d;
  transform-origin: left center;
  transition: transform 1.15s cubic-bezier(0.62, 0.04, 0.28, 1);
  z-index: 30;
  cursor: pointer;
}
.book-closed.opened {
  transform: rotateY(-112deg);
}
.book-closed.cover-hidden {
  visibility: hidden;
}
.cover {
  position: absolute;
  inset: 0;
  transform-style: preserve-3d;
}
.cover-face {
  position: absolute;
  inset: 0;
  backface-visibility: hidden;
  -webkit-backface-visibility: hidden;
  border-radius: 10px 22px 22px 10px;
  overflow: hidden;
}
.cover-face.back {
  transform: rotateY(180deg);
  background: linear-gradient(165deg, #1c1440, #140e30 70%, #191234);
  border: 1px solid rgba(212, 175, 55, 0.35);
  box-shadow: inset 0 0 80px rgba(0, 0, 0, 0.55);
}
.cb-pattern {
  position: absolute;
  inset: 0;
  background-image:
    radial-gradient(circle at 20% 25%, rgba(255, 255, 255, 0.045) 1px, transparent 1.6px),
    radial-gradient(circle at 70% 60%, rgba(255, 255, 255, 0.04) 1px, transparent 1.6px),
    radial-gradient(circle at 40% 80%, rgba(255, 255, 255, 0.05) 1px, transparent 1.6px);
  background-size: 130px 130px, 180px 180px, 110px 110px;
}
.cb-circle {
  position: absolute;
  left: 50%;
  top: 44%;
  width: 46%;
  transform: translate(-50%, -50%);
  opacity: 0.85;
}
.cb-title {
  position: absolute;
  left: 0;
  right: 0;
  top: 68%;
  text-align: center;
  font-family: var(--font-brush);
  font-size: clamp(20px, 2.4vh, 30px);
  letter-spacing: 0.22em;
  color: rgba(243, 220, 148, 0.8);
}
.cb-sub {
  position: absolute;
  left: 0;
  right: 0;
  top: 76%;
  text-align: center;
  font-family: Georgia, serif;
  font-size: clamp(8px, 1vh, 11px);
  letter-spacing: 0.5em;
  color: rgba(243, 220, 148, 0.45);
}

/* 封面正面 */
.cover-face.front {
  background:
    linear-gradient(167deg, rgba(255, 255, 255, 0.055), transparent 38%),
    linear-gradient(167deg, #171238 0%, #241a4d 46%, #131030 100%);
  border: 1px solid rgba(212, 175, 55, 0.42);
  box-shadow:
    inset 0 0 70px rgba(0, 0, 0, 0.62),
    inset 0 1px 0 rgba(255, 255, 255, 0.09),
    0 30px 70px rgba(0, 0, 0, 0.55),
    0 8px 22px rgba(0, 0, 0, 0.4);
}
.cover-face.front::after {
  content: '';
  position: absolute;
  inset: 0;
  background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='140' height='140'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.8' numOctaves='2'/><feColorMatrix values='0 0 0 0 0.62 0 0 0 0 0.55 0 0 0 0 0.48 0 0 0 0.05 0'/></filter><rect width='140' height='140' filter='url(%23n)'/></svg>");
  opacity: 0.16;
  pointer-events: none;
}
.cover-face.front::before {
  content: '';
  position: absolute;
  inset: 0;
  background-image:
    radial-gradient(circle at 22% 18%, rgba(255, 255, 255, 0.07) 1px, transparent 1.5px),
    radial-gradient(circle at 68% 42%, rgba(255, 255, 255, 0.055) 1px, transparent 1.5px),
    radial-gradient(circle at 40% 74%, rgba(255, 255, 255, 0.06) 1px, transparent 1.5px),
    radial-gradient(circle at 84% 82%, rgba(255, 255, 255, 0.05) 1px, transparent 1.5px);
  background-size: 120px 120px, 160px 160px, 100px 100px, 140px 140px;
  pointer-events: none;
}

.cf-border {
  position: absolute;
  inset: 13px;
  border: 1px solid rgba(212, 175, 55, 0.55);
  border-radius: 6px 14px 14px 6px;
  box-shadow: inset 0 0 0 3px rgba(212, 175, 55, 0.1), inset 0 0 24px rgba(0, 0, 0, 0.35);
  pointer-events: none;
}
.cf-corner {
  position: absolute;
  width: 54px;
  height: 54px;
  pointer-events: none;
  filter: drop-shadow(0 1px 3px rgba(0, 0, 0, 0.5));
}
.cf-corner.tl { top: 16px; left: 16px; }
.cf-corner.tr { top: 16px; right: 16px; transform: scaleX(-1); }
.cf-corner.bl { bottom: 16px; left: 16px; transform: scaleY(-1); }
.cf-corner.br { bottom: 16px; right: 16px; transform: scale(-1); }

.cf-body {
  position: absolute;
  inset: 26px 22px 20px 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
}
.cf-eyebrow {
  font-family: Georgia, serif;
  font-size: clamp(8px, 0.9vh, 11px);
  letter-spacing: 0.44em;
  color: rgba(243, 220, 148, 0.6);
  margin-top: 1.5%;
}
.cf-title {
  font-family: var(--font-brush);
  font-weight: normal;
  font-size: clamp(34px, 4.6vh, 56px);
  letter-spacing: 0.15em;
  margin: 1.6% 0 0.4%;
  background: linear-gradient(180deg, #fdf6d8 0%, #ecd27a 34%, #b98a2f 55%, #f6e3a6 76%, #c89e43 100%);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  filter: drop-shadow(0 2px 3px rgba(0, 0, 0, 0.55)) drop-shadow(0 0 20px rgba(233, 201, 107, 0.28));
}
.cf-titleline {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 62%;
  margin-bottom: 2%;
}
.cf-titleline .line {
  flex: 1;
  height: 1px;
  background: linear-gradient(90deg, transparent, rgba(212, 175, 55, 0.7), transparent);
}
.flourish {
  width: 34px;
  height: 14px;
  filter: drop-shadow(0 0 4px rgba(212, 175, 55, 0.5));
}

/* 封面立绘画框（凸起UV质感） */
.cf-art {
  position: relative;
  width: 76%;
  flex: 1;
  min-height: 0;
  margin: 0.8% 0;
}
.cf-art-inner {
  position: absolute;
  inset: 0;
  border-radius: 6px;
  overflow: hidden;
  border: 1px solid rgba(243, 220, 148, 0.65);
  box-shadow:
    inset 0 0 0 2px rgba(30, 20, 60, 0.75),
    inset 0 0 0 4px rgba(212, 175, 55, 0.32),
    inset 0 0 26px rgba(0, 0, 0, 0.55),
    0 6px 18px rgba(0, 0, 0, 0.5),
    0 0 26px rgba(180, 150, 255, 0.16);
  transform: translateZ(1px);
}
.cf-art-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.cf-art-shine {
  position: absolute;
  inset: 0;
  background: linear-gradient(115deg, rgba(255, 255, 255, 0.14) 0%, transparent 32%);
  pointer-events: none;
}
.cf-art-stud {
  position: absolute;
  width: 11px;
  height: 11px;
  border-radius: 50%;
  background: radial-gradient(circle at 34% 30%, #fbeec2, #c9a23c 55%, #7d5d18);
  box-shadow: 0 0 7px rgba(243, 220, 148, 0.55), inset 0 -2px 3px rgba(0, 0, 0, 0.4);
  z-index: 2;
}
.cf-art-stud.tl { top: -5px; left: -5px; }
.cf-art-stud.tr { top: -5px; right: -5px; }
.cf-art-stud.bl { bottom: -5px; left: -5px; }
.cf-art-stud.br { bottom: -5px; right: -5px; }

/* 镂空圆窗 */
.cf-window {
  position: relative;
  width: clamp(84px, 11vh, 120px);
  aspect-ratio: 1;
  margin-top: 2.6%;
}
.cf-window-hole {
  position: absolute;
  inset: 0;
  border-radius: 50%;
  background: #efe2c2;
  overflow: hidden;
  border: 4px solid;
  border-image: linear-gradient(180deg, #f3dc94, #b98a2f) 1;
  box-shadow:
    inset 0 0 0 3px rgba(120, 90, 30, 0.35),
    inset 0 6px 18px rgba(80, 55, 15, 0.55),
    inset 0 -4px 12px rgba(255, 250, 225, 0.5),
    0 4px 14px rgba(0, 0, 0, 0.5),
    0 0 22px rgba(243, 220, 148, 0.25);
}
.cf-window-page {
  position: absolute;
  inset: 0;
  background:
    linear-gradient(150deg, #f4e7c6, #e9d8ae 55%, #e2cda0),
    url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='100' height='100'><filter id='p'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2'/><feColorMatrix values='0 0 0 0 0.5 0 0 0 0 0.4 0 0 0 0 0.25 0 0 0 0.05 0'/></filter><rect width='100' height='100' filter='url(%23p)'/></svg>");
  background-blend-mode: multiply;
  opacity: 0.9;
}
.cf-circle {
  position: absolute;
  left: 6%;
  top: 6%;
  width: 88%;
  height: 88%;
  animation: spin 34s linear infinite;
}
.cf-circle.ring-inner {
  animation: spin-rev 26s linear infinite;
}
.cf-circle.pentagram {
  animation: spin-rev 44s linear infinite;
  left: 14%;
  top: 14%;
  width: 72%;
  height: 72%;
  filter: drop-shadow(0 0 6px rgba(255, 220, 130, 0.8));
}
@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
@keyframes spin-rev {
  from { transform: rotate(360deg); }
  to { transform: rotate(0deg); }
}
.cf-window-glow {
  position: absolute;
  inset: 12%;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(255, 224, 140, 0.42), rgba(255, 224, 140, 0.06) 62%, transparent 75%);
  animation: window-pulse 3.4s ease-in-out infinite;
  pointer-events: none;
}
.cf-window-core {
  position: absolute;
  left: 50%;
  top: 50%;
  width: 7px;
  height: 7px;
  transform: translate(-50%, -50%);
  border-radius: 50%;
  background: #fff3cf;
  box-shadow: 0 0 10px 4px rgba(255, 224, 140, 0.9);
  animation: window-pulse 2.6s ease-in-out infinite;
}
@keyframes window-pulse {
  0%, 100% { opacity: 0.7; transform: translate(-50%, -50%) scale(0.92); }
  50% { opacity: 1; transform: translate(-50%, -50%) scale(1.08); }
}

/* ================= 大图预览 ================= */
.preview-mask {
  position: fixed;
  inset: 0;
  z-index: 90;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(8, 4, 22, 0.86);
  backdrop-filter: blur(6px);
  cursor: pointer;
}
.preview-img {
  max-width: 84vw;
  max-height: 84vh;
  border-radius: 14px;
  border: 1px solid rgba(243, 220, 148, 0.4);
  box-shadow: 0 0 80px rgba(190, 150, 255, 0.5), 0 30px 80px rgba(0, 0, 0, 0.7);
  user-select: none;
  -webkit-user-drag: none;
}
.pv-enter-active {
  animation: pv-zoom 0.32s cubic-bezier(0.2, 0.7, 0.3, 1.15) both;
}
.pv-leave-active {
  animation: pv-zoom 0.18s ease reverse both;
}
@keyframes pv-zoom {
  from { transform: scale(0.82); opacity: 0; }
  to { transform: scale(1); opacity: 1; }
}
.preview-tip {
  position: absolute;
  bottom: 26px;
  left: 0;
  right: 0;
  text-align: center;
  font-family: var(--font-star);
  font-size: 13px;
  letter-spacing: 0.2em;
  color: rgba(240, 226, 255, 0.8);
  pointer-events: none;
}

.cf-foot {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 2.2%;
}
.cf-foot-text {
  font-family: var(--font-star);
  font-size: clamp(10px, 1.2vh, 13px);
  letter-spacing: 0.2em;
  color: rgba(243, 220, 148, 0.75);
}
.cf-seal {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border-radius: 6px;
  background: linear-gradient(145deg, #e03c4f, #a81f33);
  color: #ffe9d8;
  font-family: var(--font-brush);
  font-size: 14px;
  box-shadow: inset 0 1px 2px rgba(255, 255, 255, 0.35), 0 3px 8px rgba(0, 0, 0, 0.45);
  transform: rotate(-6deg);
}

/* ================= 展开的书页 ================= */
.book-open-view {
  position: absolute;
  inset: 0;
  z-index: 10;
}
.spread {
  position: absolute;
  inset: 0;
  display: flex;
  perspective: 2400px;
  transform-style: preserve-3d;
}
.spread::before {
  content: '';
  position: absolute;
  inset: -34px;
  z-index: 5;
  pointer-events: none;
  background: radial-gradient(ellipse at 50% 58%, rgba(255, 178, 118, 0.3), transparent 66%);
  opacity: 0;
  transition: opacity 1.1s ease;
  animation: heat-breathe 3.4s ease-in-out infinite;
  border-radius: 24px;
}
@keyframes heat-breathe {
  0%, 100% { opacity: 0.45; }
  50% { opacity: 0.95; }
}

.page {
  position: relative;
  width: 50%;
  height: 100%;
  overflow: hidden;
  z-index: 1;
  cursor: pointer;
  background: linear-gradient(180deg, rgba(255,255,255,0.16), rgba(132,96,42,0.02));
}
.page-left {
  border-radius: 10px 2px 2px 10px;
  box-shadow:
    inset 16px 0 30px -20px rgba(78, 48, 10, 0.5),
    inset -2px 0 6px rgba(90, 58, 16, 0.28),
    -14px 0 34px rgba(0, 0, 0, 0.42);
}
.page-left::after {
  content: '';
  position: absolute;
  inset: 0 0 0 auto;
  width: 10px;
  background: linear-gradient(90deg, rgba(121,91,50,0.08), rgba(121,91,50,0.18), rgba(255,255,255,0.24), rgba(121,91,50,0.08));
  pointer-events: none;
}
.page-right {
  border-radius: 2px 10px 10px 2px;
  box-shadow:
    inset -16px 0 30px -20px rgba(78, 48, 10, 0.5),
    inset 2px 0 6px rgba(90, 58, 16, 0.28),
    14px 0 34px rgba(0, 0, 0, 0.42);
}
.page-right::after {
  content: '';
  position: absolute;
  inset: 0 auto 0 0;
  width: 12px;
  background: linear-gradient(90deg, rgba(255,255,255,0.18), rgba(121,91,50,0.12), rgba(121,91,50,0.06), transparent);
  pointer-events: none;
}
.page-inner {
  position: absolute;
  inset: 0;
  overflow: hidden;
}

/* ================= 翻页动画 ================= */
.flip-anim {
  position: absolute;
  top: 0;
  height: 100%;
  width: 50%;
  transform-style: preserve-3d;
  z-index: 20;
  pointer-events: none;
  will-change: transform, opacity;
  filter: none;
}
.flip-anim.fwd {
  left: 50%;
  transform-origin: left center;
}
.flip-anim.bwd {
  left: 0;
  transform-origin: right center;
}
.flip-anim.fwd.flipping {
  animation: flip-fwd 0.82s cubic-bezier(0.2, 0.75, 0.2, 1) forwards;
}
.flip-anim.bwd.flipping {
  animation: flip-bwd 0.82s cubic-bezier(0.2, 0.75, 0.2, 1) forwards;
}
@keyframes flip-fwd {
  0% {
    transform: rotateY(0deg) scaleX(1) translateZ(0);
    opacity: 1;
  }
  16% {
    transform: rotateY(-18deg) scaleX(1.02) translateZ(2px);
  }
  52% {
    transform: rotateY(-122deg) scaleX(0.992) translateZ(4px);
    opacity: 1;
  }
  80% {
    transform: rotateY(-168deg) scaleX(0.965) translateZ(1px);
    opacity: 1;
  }
  100% {
    transform: rotateY(-180deg) scaleX(0.95) translateZ(0);
    opacity: 0;
  }
}
@keyframes flip-bwd {
  0% {
    transform: rotateY(0deg) scaleX(1) translateZ(0);
    opacity: 1;
  }
  16% {
    transform: rotateY(18deg) scaleX(1.02) translateZ(2px);
  }
  52% {
    transform: rotateY(122deg) scaleX(0.992) translateZ(4px);
    opacity: 1;
  }
  80% {
    transform: rotateY(168deg) scaleX(0.965) translateZ(1px);
    opacity: 1;
  }
  100% {
    transform: rotateY(180deg) scaleX(0.95) translateZ(0);
    opacity: 0;
  }
}
.flip-anim::after {
  content: '';
  position: absolute;
  inset: 0;
  z-index: 6;
  pointer-events: none;
  opacity: 0;
}
.flip-anim.fwd.flipping::after {
  animation: flip-shadow-fwd 0.82s ease forwards;
}
.flip-anim.bwd.flipping::after {
  animation: flip-shadow-bwd 0.82s ease forwards;
}
@keyframes flip-shadow-fwd {
  0% { background: linear-gradient(90deg, rgba(60, 34, 8, 0.5), rgba(60, 34, 8, 0.18) 38%, transparent 60%); opacity: 0; }
  18% { opacity: 0.7; }
  60% { opacity: 0.92; }
  100% { background: linear-gradient(270deg, rgba(60, 34, 8, 0.5), rgba(60, 34, 8, 0.18) 38%, transparent 62%); opacity: 0; }
}
@keyframes flip-shadow-bwd {
  0% { background: linear-gradient(270deg, rgba(60, 34, 8, 0.5), rgba(60, 34, 8, 0.18) 38%, transparent 60%); opacity: 0; }
  18% { opacity: 0.7; }
  60% { opacity: 0.92; }
  100% { background: linear-gradient(90deg, rgba(60, 34, 8, 0.5), rgba(60, 34, 8, 0.18) 38%, transparent 62%); opacity: 0; }
}
.flip-face {
  position: absolute;
  inset: 0;
  backface-visibility: hidden;
  -webkit-backface-visibility: hidden;
  overflow: hidden;
  background: linear-gradient(180deg, #f7efd7 0%, #ead9b4 42%, #e2cea4 100%);
  box-shadow: inset 0 0 0 1px rgba(119, 90, 45, 0.18);
}
.flip-face::before {
  content: '';
  position: absolute;
  inset: 0;
  pointer-events: none;
  background: linear-gradient(90deg, rgba(255,255,255,0.18), transparent 28%, rgba(125,92,46,0.04) 72%, rgba(71,47,18,0.14));
  opacity: 0.6;
}
.flip-face::after {
  content: '';
  position: absolute;
  inset: 0 0 0 auto;
  width: 9px;
  background: linear-gradient(90deg, rgba(255,255,255,0.04), rgba(255,255,255,0.18), rgba(123,93,52,0.08), rgba(71,47,18,0.06));
  pointer-events: none;
  opacity: 0.7;
}
.flip-face.back {
  transform: rotateY(180deg);
  border-radius: 10px;
  box-shadow: inset 0 0 44px rgba(90, 58, 16, 0.32), 6px 0 22px rgba(0, 0, 0, 0.35);
}
.flip-back-paper {
  position: absolute;
  inset: 0;
  background:
    linear-gradient(150deg, #f2e5c6, #e9d8b2 60%, #e0cba0),
    url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='100' height='100'><filter id='b'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2'/><feColorMatrix values='0 0 0 0 0.5 0 0 0 0 0.4 0 0 0 0 0.25 0 0 0 0.05 0'/></filter><rect width='100' height='100' filter='url(%23b)'/></svg>");
  background-blend-mode: multiply;
}
.flip-back-circle {
  position: absolute;
  left: 50%;
  top: 50%;
  width: 52%;
  transform: translate(-50%, -50%);
  opacity: 0.8;
}

/* 翻页星光 */
.dust-layer {
  position: absolute;
  inset: 0;
  z-index: 25;
  pointer-events: none;
  overflow: visible;
}
.dust-star {
  position: absolute;
  border-radius: 50%;
  animation: dust-fly 1.2s cubic-bezier(0.2, 0.55, 0.3, 1) forwards;
  opacity: 0;
}
@keyframes dust-fly {
  0% {
    transform: translate(-50%, -50%) rotate(0deg) scale(1);
    opacity: 0;
  }
  12% { opacity: 1; }
  100% {
    transform: translate(calc(-50% + var(--dx, 0px)), calc(-50% + var(--dy, 0px))) rotate(var(--rot, 180deg)) scale(0.1);
    opacity: 0;
  }
}

/* ================= 立绘空间（书倒下后的底座舞台） ================= */
.blossom-space {
  position: absolute;
  inset: 0;
  z-index: 15;
  cursor: pointer;
  background:
    radial-gradient(50% 36% at 50% 82%, rgba(222, 190, 255, 0.42), transparent 72%),
    radial-gradient(46% 34% at 50% 30%, rgba(168, 140, 240, 0.2), transparent 70%);
  animation: blossom-fade 0.8s ease both;
}
.blossom-base {
  position: absolute;
  left: 50%;
  bottom: 9%;
  width: 66%;
  aspect-ratio: 3.1 / 1;
  transform: translateX(-50%);
  border-radius: 50%;
  background:
    radial-gradient(ellipse at 50% 45%, rgba(240, 226, 255, 0.95), rgba(204, 172, 255, 0.55) 46%, rgba(168, 132, 240, 0.18) 70%, transparent 82%);
  box-shadow:
    0 0 84px rgba(198, 158, 255, 0.55),
    0 0 26px rgba(230, 214, 255, 0.65),
    inset 0 0 60px rgba(242, 232, 255, 0.6);
  animation: base-grow 1.05s cubic-bezier(0.22, 0.68, 0.3, 1) both;
  pointer-events: none;
  z-index: 1;
}
@keyframes base-grow {
  from { transform: translateX(-50%) scale(0.3); opacity: 0; }
  55% { opacity: 1; }
  to { transform: translateX(-50%) scale(1); opacity: 1; }
}
@keyframes blossom-fade {
  from { opacity: 0; transform: scale(1.06); }
  to { opacity: 1; transform: scale(1); }
}
.tree-canvas {
  position: absolute;
  inset: 0;
  z-index: 2;
}
.girls-wrap {
  position: absolute;
  left: 50%;
  bottom: 3%;
  width: 46%;
  transform: translateX(-50%);
  z-index: 4;
  display: flex;
  flex-direction: column;
  align-items: center;
  pointer-events: none;
  animation: girls-rise 1.35s cubic-bezier(0.2, 0.6, 0.25, 1) 0.55s both,
             girls-float 4.6s ease-in-out 2.3s infinite;
}
@keyframes girls-rise {
  from { transform: translateX(-50%) translateY(104%); }
  to { transform: translateX(-50%) translateY(0); }
}

/* 合上底座：立绘自上而下沉降消失 */
.girls-wrap.girls-sinking {
  animation: girls-sink 0.9s cubic-bezier(0.55, 0.06, 0.68, 0.19) both;
}
@keyframes girls-sink {
  from { transform: translateX(-50%) translateY(0); opacity: 1; }
  to { transform: translateX(-50%) translateY(108%); opacity: 0; }
}
.blossom-base.blossom-fade {
  animation: base-fade 0.8s ease both;
}
@keyframes base-fade {
  to { opacity: 0; transform: translateX(-50%) scale(0.9); }
}
.tree-canvas {
  transition: opacity 0.85s ease;
}
.tree-canvas.tree-fade {
  opacity: 0;
}
.front-petals {
  transition: opacity 0.8s ease;
}
.front-petals.petals-fade {
  opacity: 0;
}
.blossom-hint {
  transition: opacity 0.6s ease;
}
.blossom-hint.hint-fade {
  opacity: 0;
}
.girl-shadow {
  position: absolute;
  bottom: 3.5%;
  left: 50%;
  width: 84%;
  height: 9%;
  transform: translateX(-50%);
  background: radial-gradient(ellipse at center, rgba(120, 70, 160, 0.55), transparent 68%);
  filter: blur(8px);
}
.girls-img {
  width: 100%;
  height: auto;
  display: block;
  border-radius: 26px;
  -webkit-mask-image: radial-gradient(ellipse 58% 62% at 50% 58%, #000 52%, transparent 78%);
  mask-image: radial-gradient(ellipse 58% 62% at 50% 58%, #000 52%, transparent 78%);
  filter: drop-shadow(0 12px 26px rgba(92, 40, 150, 0.4));
}
@keyframes girls-float {
  0%, 100% { transform: translateX(-50%) translateY(0) rotate(-0.6deg); }
  50% { transform: translateX(-50%) translateY(-13px) rotate(0.8deg); }
}
.front-petals {
  position: absolute;
  inset: 0;
  z-index: 6;
  pointer-events: none;
  overflow: hidden;
}
.front-petal {
  position: absolute;
  border-radius: 62% 38% 58% 42% / 60% 52% 48% 40%;
  background: linear-gradient(135deg, #ffeef3, #ffc9da 70%, #ffb3cd);
  box-shadow: 0 0 7px rgba(255, 176, 202, 0.55);
  animation: petal-fall linear infinite;
}
@keyframes petal-fall {
  0% { transform: translateY(0) translateX(0) rotate(0deg); opacity: 0; }
  8% { opacity: 1; }
  60% { transform: translateY(52vh) translateX(6vw) rotate(160deg); }
  100% { transform: translateY(96vh) translateX(-5vw) rotate(340deg); opacity: 0.9; }
}
.blossom-hint {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 2.5%;
  text-align: center;
  z-index: 8;
  font-family: var(--font-star);
  font-size: 13px;
  letter-spacing: 0.24em;
  color: rgba(243, 226, 255, 0.85);
  text-shadow: 0 0 14px rgba(200, 160, 255, 0.8);
  pointer-events: none;
  animation: hint-breathe 3s ease-in-out infinite;
}
@keyframes hint-breathe {
  0%, 100% { opacity: 0.65; }
  50% { opacity: 1; }
}
</style>
